/**
 * Cached state of the `excelUseClaudeOpus` ConfigCat flag, which selects the
 * model the Excel executor runs on: off (or unresolved) serves GPT-5.5, on
 * serves Claude Opus 5.
 *
 * The ConfigCat client lives in the PowerPoint workspace, which `excel/` and
 * `shared/` may not import (see `tools/import-boundary-check`). So the service
 * pushes the resolved value in here and the executor reads it back out,
 * mirroring how `shared/office/officeJsBypass.ts` carries `bypassOfficeJsApi`.
 * This module stays boolean-only and holds no model ids, so `shared/` gains no
 * dependency on `excel/`.
 *
 * Opus costs roughly 10x GPT per query, so every unresolved state — no SDK key,
 * disabled client, pending refresh, ConfigCat unreachable — has to read as
 * disabled rather than enabled.
 */

let excelUseClaudeOpus: boolean | null = false;
let excelClaudeOpusFlagReader: (() => boolean) | null = null;

export function registerExcelClaudeOpusFlagReader(reader: (() => boolean) | null): void {
  excelClaudeOpusFlagReader = reader;
}

export function setExcelClaudeOpusFlag(value: boolean): void {
  excelUseClaudeOpus = value;
}

export function resetExcelClaudeOpusFlag(): void {
  excelUseClaudeOpus = false;
}

export function invalidateExcelClaudeOpusFlag(): void {
  excelUseClaudeOpus = null;
}

export function isExcelClaudeOpusEnabled(): boolean {
  if (excelClaudeOpusFlagReader) {
    // A reader that throws must not take the run down with it, and must not be
    // read as "enabled" — the expensive model is never the safe guess.
    try {
      return excelClaudeOpusFlagReader() === true;
    } catch {
      return false;
    }
  }
  return excelUseClaudeOpus === true;
}
